import React, { useRef, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './WaitlistBar.css';
import { useWaterRipple } from '../../hooks/useWaterRipple';
import { LearnMoreModal } from './LearnMoreModal';

type WaitlistBarProps = {
  onOpen: () => void;
  onPrivateCircleClick?: () => void;
};

export const WaitlistBar: React.FC<WaitlistBarProps> = ({ onOpen, onPrivateCircleClick }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { canvasRef, triggerWave } = useWaterRipple(containerRef);
  const isPressing = useRef(false);
  const hoverIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [isLearnMoreOpen, setIsLearnMoreOpen] = useState(false);
  const navigate = useNavigate();

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (hoverIntervalRef.current) clearInterval(hoverIntervalRef.current);
    };
  }, []);

  // Returns button center in viewport (clientX/Y) coordinates
  const getButtonCenter = () => {
    const btn = buttonRef.current;
    if (!btn) return null;
    const r = btn.getBoundingClientRect();
    return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
  };

  // Spawn a dramatic splash of particles flying out from the given viewport point
  const spawnParticles = useCallback((clientX: number, clientY: number) => {
    const container = containerRef.current;
    if (!container) return;
    const cr = container.getBoundingClientRect();
    const cx = clientX - cr.left;
    const cy = clientY - cr.top;

    // Gradient-matching colours so particles blend with the banner
    const COLORS = [
      '#ffffff',
      'rgba(190,170,255,0.95)',
      'rgba(255,150,210,0.95)',
      'rgba(255,210,130,0.95)',
      'rgba(150,190,255,0.95)',
    ];

    // Two waves: big immediate burst + smaller trailing burst with delay
    const WAVES = [
      { count: 16, minDist: 70, maxDist: 130, minSize: 5,  maxSize: 13, delay: 0   },
      { count: 10, minDist: 45, maxDist:  90, minSize: 3,  maxSize:  8, delay: 70  },
    ];

    WAVES.forEach(({ count, minDist, maxDist, minSize, maxSize, delay }) => {
      for (let i = 0; i < count; i++) {
        const angle    = (i / count) * Math.PI * 2 + (Math.random() - 0.5) * 0.7;
        const dist     = minDist + Math.random() * (maxDist - minDist);
        const size     = minSize + Math.random() * (maxSize - minSize);
        const color    = COLORS[Math.floor(Math.random() * COLORS.length)];
        const duration = 520 + Math.random() * 220;

        const el = document.createElement('span');
        el.className = 'waitlist-particle';
        el.style.cssText = [
          `left:${cx}px`,
          `top:${cy}px`,
          `width:${size}px`,
          `height:${size}px`,
          `--tx:${(Math.cos(angle) * dist).toFixed(1)}px`,
          `--ty:${(Math.sin(angle) * dist).toFixed(1)}px`,
          `--pcolor:${color}`,
          `animation-delay:${delay}ms`,
          `animation-duration:${duration}ms`,
        ].join(';');
        container.appendChild(el);
        el.addEventListener('animationend', () => el.remove(), { once: true });
      }
    });
  }, []);

  // Button hover → continuous concentric rings emanating from button center
  const handleButtonEnter = useCallback(() => {
    if (hoverIntervalRef.current) return; // already running
    const fire = () => {
      const c = getButtonCenter();
      if (c) triggerWave(c.x, c.y, 380, 20);
    };
    fire(); // immediate first ring
    hoverIntervalRef.current = setInterval(fire, 260);
  }, [triggerWave]);

  // Press down → visual press state
  const handleButtonDown = useCallback(() => {
    isPressing.current = true;
    buttonRef.current?.classList.add('is-pressing');
  }, []);

  // Release → bounce back + big wave
  const handleButtonUp = useCallback(() => {
    if (!isPressing.current) return;
    isPressing.current = false;
    buttonRef.current?.classList.remove('is-pressing');
    const c = getButtonCenter();
    if (!c) return;
    triggerWave(c.x, c.y, 600, 32);
  }, [triggerWave]);

  // Stop rings + cancel press state when mouse leaves button
  const handleButtonLeave = useCallback(() => {
    if (hoverIntervalRef.current) {
      clearInterval(hoverIntervalRef.current);
      hoverIntervalRef.current = null;
    }
    if (isPressing.current) {
      isPressing.current = false;
      buttonRef.current?.classList.remove('is-pressing');
    }
  }, []);

  const handleLearnMore = () => {
    if (onPrivateCircleClick) {
      onPrivateCircleClick();
      return;
    }
    navigate('/private-circle');
  };

  return (
    <div ref={containerRef} className="waitlist-bar" role="region" aria-label="Заявка в закрытое сообщество">
      <canvas ref={canvasRef} className="waitlist-bar__canvas" aria-hidden="true" />
      <div className="waitlist-bar__inner">
        <span className="waitlist-bar__title">
          datacircle анонсирует запуск закрытого сообщества
        </span>
        <button
          type="button"
          className="waitlist-bar__more-btn"
          onClick={handleLearnMore}
        >
          Подробнее →
        </button>
      </div>

      <LearnMoreModal
        isOpen={isLearnMoreOpen}
        onClose={() => setIsLearnMoreOpen(false)}
        onOpenWaitlist={onOpen}
      />
    </div>
  );
};
